import { useState, useMemo } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import {
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Star,
  Copy,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
} from "lucide-react"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"
import { useCustomerContacts, useCustomerContactsList } from "@/hooks/useCustomerContacts"
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog"
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences"
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration"

interface Contact {
  id: string
  title?: string
  firstName: string
  surname: string
  email: string
  phone: string
  mobile?: string
  preferredContactMethod?: string
  jobTitle: string
  company: string
  customerId: string
  notes: string
  createdAt: string
  lastContact: string
  isPrimary: boolean
}

interface AllContactsTabProps {
  customerId: string
  customerName: string
}

const DEFAULT_CONTACT_COLUMNS: ColumnDefinition[] = [
  { id: 'isPrimary', label: 'Primary', visible: true, order: 0 },
  { id: 'title', label: 'Title', visible: false, order: 1 },
  { id: 'firstName', label: 'First Name', visible: true, order: 2 },
  { id: 'surname', label: 'Surname', visible: true, order: 3 },
  { id: 'email', label: 'Email', visible: true, order: 4 },
  { id: 'phone', label: 'Phone', visible: true, order: 5 },
  { id: 'mobile', label: 'Mobile', visible: false, order: 6 },
  { id: 'preferredContactMethod', label: 'Preferred Contact Method', visible: false, order: 7 },
  { id: 'jobTitle', label: 'Job Title', visible: true, order: 8 },
  { id: 'company', label: 'Company', visible: true, order: 9 },
  { id: 'notes', label: 'Notes', visible: false, order: 10 },
  { id: 'lastContact', label: 'Last Contact', visible: true, order: 11 },
  { id: 'createdAt', label: 'Created At', visible: false, order: 12 },
];

type SortField = "firstName" | "surname" | "email" | "phone" | "company" | "lastContact"
type SortDirection = "asc" | "desc"

// Title options for dropdown
const TITLE_OPTIONS = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Rev."]

// Preferred Contact Method options
const PREFERRED_CONTACT_METHOD_OPTIONS = ["Email", "SMS", "Phone", "Customer Portal"]

// Mock contacts data (6 contacts total, some customers share contacts)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _initialContacts: Contact[] = [
  {
    id: "1",
    firstName: "John",
    surname: "Smith",
    email: "john.smith@acmecorp.com",
    phone: "+1 (555) 123-4567",
    jobTitle: "Accounts Payable Manager",
    company: "Acme Corporation",
    customerId: "1",
    notes: "Primary contact for accounts payable. Prefers email communication.",
    createdAt: "2024-01-15",
    lastContact: "2024-03-10",
    isPrimary: true,
  },
  {
    id: "2",
    firstName: "Sarah",
    surname: "Johnson",
    email: "sarah.j@acmecorp.com",
    phone: "+1 (555) 123-4568",
    jobTitle: "Assistant Controller",
    company: "Acme Corporation",
    customerId: "1",
    notes: "Backup contact. Available for urgent matters.",
    createdAt: "2024-01-20",
    lastContact: "2024-02-28",
    isPrimary: false,
  },
  {
    id: "3",
    firstName: "Michael",
    surname: "Chen",
    email: "m.chen@techsolutions.com",
    phone: "+1 (555) 234-5678",
    jobTitle: "Chief Financial Officer",
    company: "Tech Solutions Inc",
    customerId: "2",
    notes: "CFO. Handles all payment approvals.",
    createdAt: "2024-02-01",
    lastContact: "2024-03-08",
    isPrimary: true,
  },
  {
    id: "4",
    firstName: "Emily",
    surname: "Rodriguez",
    email: "emily.r@globaltrading.com",
    phone: "+1 (555) 345-6789",
    jobTitle: "Accounts Manager",
    company: "Global Trading Ltd",
    customerId: "3",
    notes: "Accounts Manager. Very responsive to emails.",
    createdAt: "2024-01-10",
    lastContact: "2024-03-12",
    isPrimary: true,
  },
  {
    id: "5",
    firstName: "David",
    surname: "Park",
    email: "d.park@innovativedesigns.com",
    phone: "+1 (555) 456-7890",
    jobTitle: "Owner & CEO",
    company: "Innovative Designs Co",
    customerId: "4",
    notes: "Owner. Prefers phone calls for urgent matters.",
    createdAt: "2024-02-15",
    lastContact: "2024-03-05",
    isPrimary: true,
  },
  {
    id: "6",
    firstName: "Lisa",
    surname: "Anderson",
    email: "l.anderson@manufacturingpro.com",
    phone: "+1 (555) 567-8901",
    jobTitle: "Finance Director",
    company: "Manufacturing Pro LLC",
    customerId: "5",
    notes: "Finance Director. Requires invoice copies via email.",
    createdAt: "2024-01-25",
    lastContact: "2024-03-11",
    isPrimary: true,
  },
]

export function AllContactsTab({ customerId, customerName }: AllContactsTabProps) {
  // Mode-aware contacts query
  const { data: queryContacts = [] } = useCustomerContactsList(customerId);
  const contacts = queryContacts as Contact[];

  const [searchQuery, setSearchQuery] = useState("")
  const [sortField, setSortField] = useState<SortField>("firstName")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false)
  const {
    columns: contactColumns,
    visibleColumns: contactVisibleColumns,
    savePreferences: saveContactColumnPreferences,
    resetToDefaults: resetContactColumnPreferences,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Contacts, DEFAULT_CONTACT_COLUMNS)
  const visibleColumns = contactVisibleColumns.map((col) => col.id)

  const { 
    createContact, 
    updateContact, 
    deleteContact, 
    setContactAsPrimary 
  } = useCustomerContacts(customerId);

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    mobile: "",
    preferredContactMethod: "",
    jobTitle: "",
    company: customerName,
    notes: "",
    isPrimary: false,
  })

  // Filter contacts by customer and search query
  const filteredContacts = useMemo(() => {
    let filtered = contacts.filter((contact) => contact.customerId === customerId)

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (contact) =>
          contact.firstName.toLowerCase().includes(query) ||
          contact.surname.toLowerCase().includes(query) ||
          (contact.title && contact.title.toLowerCase().includes(query)) ||
          contact.email.toLowerCase().includes(query) ||
          contact.phone.includes(query) ||
          contact.company.toLowerCase().includes(query)
      )
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      if (typeof aValue === "string") aValue = aValue.toLowerCase()
      if (typeof bValue === "string") bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
      return 0
    })

    return filtered
  }, [contacts, customerId, searchQuery, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    )
  }

  const handleAddContact = async () => {
    // Validation
    if (!formData.firstName || !formData.surname || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields")
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address")
      return
    }

    const phoneRegex = /^[0-9\s\-+().]+$/
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Please enter a valid phone number (no letters allowed)")
      return
    }

    // Mobile validation (if provided)
    if (formData.mobile && !phoneRegex.test(formData.mobile)) {
      toast.error("Please enter a valid mobile number (no letters allowed)")
      return
    }

    try {
      await createContact({
        title: formData.title || undefined,
        firstName: formData.firstName,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile || undefined,
        preferredContactMethod: formData.preferredContactMethod || undefined,
        jobTitle: formData.jobTitle,
        company: formData.company,
        notes: formData.notes,
        isPrimary: formData.isPrimary,
      })

      setIsAddDialogOpen(false)
      resetForm()
      toast.success("Contact added successfully")
    } catch (error) {
      console.error("Error creating contact:", error)
      toast.error(error instanceof Error ? error.message : "Failed to create contact")
    }
  }

  const handleEditContact = async () => {
    if (!selectedContact) return

    // Validation
    if (!formData.firstName || !formData.surname || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields")
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address")
      return
    }

    const phoneRegex = /^[0-9\s\-+().]+$/
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Please enter a valid phone number (no letters allowed)")
      return
    }

    // Mobile validation (if provided)
    if (formData.mobile && !phoneRegex.test(formData.mobile)) {
      toast.error("Please enter a valid mobile number (no letters allowed)")
      return
    }

    try {
      await updateContact(selectedContact.id, {
        title: formData.title || undefined,
        firstName: formData.firstName,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile || undefined,
        preferredContactMethod: formData.preferredContactMethod || undefined,
        jobTitle: formData.jobTitle,
        company: formData.company,
        notes: formData.notes,
        isPrimary: formData.isPrimary,
      })

      setIsEditDialogOpen(false)
      setSelectedContact(null)
      resetForm()
      toast.success("Contact updated successfully")
    } catch (error) {
      console.error("Error updating contact:", error)
      toast.error(error instanceof Error ? error.message : "Failed to update contact")
    }
  }

  const handleDeleteContact = async () => {
    if (!selectedContact) return

    try {
      await deleteContact(selectedContact.id)

      setIsDeleteDialogOpen(false)
      setSelectedContact(null)
      toast.success("Contact deleted successfully")
    } catch (error) {
      console.error("Error deleting contact:", error)
      toast.error(error instanceof Error ? error.message : "Failed to delete contact")
    }
  }

  const handleSetPrimary = async (contact: Contact) => {
    try {
      await setContactAsPrimary(contact.id)

      const fullName= `${contact.title ? contact.title + ' ' : ''}${contact.firstName} ${contact.surname}`
      toast.success(`${fullName} set as primary contact`)
    } catch (error) {
      console.error("Error setting primary contact:", error)
      toast.error(error instanceof Error ? error.message : "Failed to set primary contact")
    }
  }

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email)
    toast.success("Email copied to clipboard")
  }

  const openAddDialog = () => {
    resetForm()
    setFormData((prev) => ({ ...prev, company: customerName }))
    setIsAddDialogOpen(true)
  }

  const openEditDialog = (contact: Contact) => {
    setSelectedContact(contact)
    setFormData({
      title: contact.title || "",
      firstName: contact.firstName,
      surname: contact.surname,
      email: contact.email,
      phone: contact.phone,
      mobile: contact.mobile || "",
      preferredContactMethod: contact.preferredContactMethod || "",
      jobTitle: contact.jobTitle,
      company: contact.company,
      notes: contact.notes,
      isPrimary: contact.isPrimary,
    })
    setIsEditDialogOpen(true)
  }

  const openDeleteDialog = (contact: Contact) => {
    setSelectedContact(contact)
    setIsDeleteDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      title: "",
      firstName: "",
      surname: "",
      email: "",
      phone: "",
      mobile: "",
      preferredContactMethod: "",
      jobTitle: "",
      company: customerName,
      notes: "",
      isPrimary: false,
    })
  }


  // Helper function to render table cell content based on column
  const renderCellContent = (contact: Contact, columnId: string) => {
    switch (columnId) {
      case "isPrimary":
        return contact.isPrimary ? (
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        ) : null
      case "title":
        return contact.title || "-"
      case "firstName":
        return contact.firstName
      case "surname":
        return contact.surname
      case "email":
        return (
          <div className="flex items-center gap-2">
            {contact.email}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleCopyEmail(contact.email)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        )
      case "phone":
        return contact.phone
      case "mobile":
        return contact.mobile || "-"
      case "preferredContactMethod":
        return contact.preferredContactMethod || "-"
      case "jobTitle":
        return contact.jobTitle || "-"
      case "company":
        return contact.company
      case "notes":
        return (
          <div className="max-w-xs truncate" title={contact.notes}>
            {contact.notes || "-"}
          </div>
        )
      case "lastContact":
        return formatDate(contact.lastContact)
      case "createdAt":
        return formatDate(contact.createdAt)
      default:
        return null
    }
  }

  const getColumnLabel = (columnId: string) => {
    const column = DEFAULT_CONTACT_COLUMNS.find(col => col.id === columnId)
    return column?.label || columnId
  }

  // Check if column is sortable
  const isSortableColumn = (columnId: string): columnId is SortField => {
    return ["firstName", "surname", "email", "phone", "company", "lastContact"].includes(columnId)
  }

  return (
    <div className="space-y-4">
      {/* Header with Search, Configure Columns, and Add Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsColumnConfigOpen(true)}
            title="Configure Columns"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button onClick={openAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((columnId) => (
                <TableHead 
                  key={columnId}
                  className={columnId === "isPrimary" ? "w-[50px]" : undefined}
                >
                  {isSortableColumn(columnId) ? (
                    <Button
                      variant="ghost"
                      onClick={() => handleSort(columnId)}
                      className="h-8 px-2 lg:px-3"
                    >
                      {getColumnLabel(columnId)}
                      {getSortIcon(columnId)}
                    </Button>
                  ) : (
                    <span className="px-2">{getColumnLabel(columnId)}</span>
                  )}
                </TableHead>
              ))}
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} className="text-center text-muted-foreground">
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact) => (
                <TableRow key={contact.id}>
                  {visibleColumns.map((columnId) => (
                    <TableCell 
                      key={columnId}
                      className={["firstName", "surname"].includes(columnId) ? "font-medium" : undefined}
                    >
                      {renderCellContent(contact, columnId)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!contact.isPrimary && (
                          <DropdownMenuItem onClick={() => handleSetPrimary(contact)}>
                            <Star className="mr-2 h-4 w-4" />
                            Set as Primary
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(contact)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Contact Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
            <DialogDescription>
              Add a new contact for {customerName}. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-title">Title</Label>
                <Select
                  value={formData.title}
                  onValueChange={(value) => setFormData({ ...formData, title: value })}
                >
                  <SelectTrigger id="add-title">
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    {TITLE_OPTIONS.map((title) => (
                      <SelectItem key={title} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-firstName">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-surname">
                  Surname <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-surname"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  placeholder="Smith"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-email">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john.smith@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-phone">
                  Phone <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-mobile">Mobile</Label>
                <Input
                  id="add-mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="+1 (555) 987-6543"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-preferredContactMethod">Preferred Contact Method</Label>
                <Select
                  value={formData.preferredContactMethod}
                  onValueChange={(value) => setFormData({ ...formData, preferredContactMethod: value })}
                >
                  <SelectTrigger id="add-preferredContactMethod">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFERRED_CONTACT_METHOD_OPTIONS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-jobTitle">Job Title</Label>
              <Input
                id="add-jobTitle"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                placeholder="Account Manager"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-company">Company</Label>
              <Input
                id="add-company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Company Name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-notes">Notes</Label>
              <Textarea
                id="add-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this contact..."
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="add-primary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="add-primary" className="cursor-pointer">
                Set as primary contact
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddContact}>Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>
              Update contact information. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Select
                  value={formData.title}
                  onValueChange={(value) => setFormData({ ...formData, title: value })}
                >
                  <SelectTrigger id="edit-title">
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    {TITLE_OPTIONS.map((title) => (
                      <SelectItem key={title} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-surname">
                  Surname <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-surname"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  placeholder="Smith"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john.smith@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">
                  Phone <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-mobile">Mobile</Label>
                <Input
                  id="edit-mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="+1 (555) 987-6543"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-preferredContactMethod">Preferred Contact Method</Label>
                <Select
                  value={formData.preferredContactMethod}
                  onValueChange={(value) => setFormData({ ...formData, preferredContactMethod: value })}
                >
                  <SelectTrigger id="edit-preferredContactMethod">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFERRED_CONTACT_METHOD_OPTIONS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-jobTitle">Job Title</Label>
              <Input
                id="edit-jobTitle"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                placeholder="Account Manager"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company">Company</Label>
              <Input
                id="edit-company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Company Name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this contact..."
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-primary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-primary" className="cursor-pointer">
                Set as primary contact
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditContact}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedContact ? `${selectedContact.title ? selectedContact.title + ' ' : ''}${selectedContact.firstName} ${selectedContact.surname}` : 'this contact'}? This action cannot be undone.
              {selectedContact?.isPrimary && (
                <span className="block mt-2 text-amber-600 dark:text-amber-500">
                  This is the primary contact. The next contact will be automatically promoted to
                  primary.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteContact}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={contactColumns}
        onApply={saveContactColumnPreferences}
        onReset={resetContactColumnPreferences}
      />
    </div>
  )
}
